<script setup lang="ts">
import { ref } from 'vue'

import LineItemPrice from '@satch-ui/components/line/shopify/LineItemPrice.vue'
import {
  triggerDecreaseCartLineEvent,
  triggerIncreaseCartLineEvent,
} from '@shared/event/events/cart'
import MaybeLinkOrNothing from '@shared/fondof-ui/components/atoms/link/MaybeLinkOrNothing.vue'
import { useCartStore } from '@shared/global-store/stores/cart/cart'
import { updateCartLines } from '@shared/shop-api/cart/api'
import type { CartLine } from '@shared/shop-api/cart/lines/types'
import LineItem from '@storefront-satch/components/molecules/product/line/LineItem.vue'

const props = defineProps<{
  line: CartLine
  canEdit?: boolean
  linked?: boolean
  fullInfo?: boolean
}>()

const cartStore = useCartStore()

const isDeleting = ref(false)
const errorMessage = ref('')

const updateCartLineQuantity = async (id: string, quantity: number) => {
  errorMessage.value = ''

  if (quantity < 1) {
    isDeleting.value = true
  }

  cartStore.setIsProcessing(true)

  const oldCartItem = cartStore.cart?.lines.find((line) => line.id === id)
  const oldQuantity = oldCartItem?.quantity || 0

  const cart = await updateCartLines(cartStore.cart?.id as string, [
    { id, quantity },
  ])

  const newCartLine = cart?.lines.find((line) => line.id === id)
  const newQuantity = newCartLine?.quantity || 0
  const isIncrease = oldQuantity < newQuantity

  if (cart && isIncrease && newCartLine) {
    triggerIncreaseCartLineEvent({
      cart,
      cartLine: newCartLine,
      quantity: newQuantity - oldQuantity,
      added: false,
    })
  }

  if (cart && !isIncrease && oldCartItem) {
    triggerDecreaseCartLineEvent({
      cart,
      cartLine: newCartLine || { ...oldCartItem, quantity: 0 },
      quantity: newQuantity - oldQuantity,
      removed: newQuantity === 0,
    })
  }

  errorMessage.value = cart.merchandiseWarning?.message || ''

  cartStore.setCart(cart)
  cartStore.setIsProcessing(false)

  if (quantity < 1) {
    isDeleting.value = false
  }

  if (cart.lines.length === 0) {
    globalThis.location.reload()
  }
}
</script>

<template>
  <MaybeLinkOrNothing
    :href="line.variant.product?.url"
    :condition="linked && !canEdit"
    class="mb-8 transition-all duration-[250ms] last:mb-0"
    :class="{
      'opacity-50 grayscale': isDeleting,
    }"
  >
    <LineItem
      v-bind="props"
      :error-message="errorMessage"
      @add-item="updateCartLineQuantity"
      @remove-item="updateCartLineQuantity"
      @change-custom-quantity-item="updateCartLineQuantity"
    >
      <template #price="slotProps">
        <LineItemPrice
          v-bind="{ ...slotProps, ...props }"
          :is-processing="cartStore.isProcessing"
          :error-message="errorMessage"
          @add-item="updateCartLineQuantity"
          @remove-item="updateCartLineQuantity"
          @change-custom-quantity-item="updateCartLineQuantity"
        />
      </template>
    </LineItem>
  </MaybeLinkOrNothing>
</template>
